/**
 * Deep merge two objects recursively
 * @param target - Target object (base/default values)
 * @param source - Source object (override values)
 * @returns Deep merged object where source values override target values while preserving nested structure
 *
 * @example
 * ```typescript
 * const target = { a: 1, b: { x: 1, y: 2 } };
 * const source = { b: { x: 3, z: 4 }, c: 5 };
 * const result = deepMerge(target, source);
 * // Result: { a: 1, b: { x: 3, y: 2, z: 4 }, c: 5 }
 * // - Preserves target.a: 1
 * // - Preserves target.b.y: 2
 * // - Overrides target.b.x with source.b.x: 3
 * // - Adds new source.b.z: 4
 * // - Adds new source.c: 5
 * ```
 */
export const deepMerge = <T = any>(target: any, source: any): T => {
  if (!source || typeof source !== 'object') {
    return target;
  }

  if (!target || typeof target !== 'object') {
    return source;
  }

  const result = { ...target };

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      if (
        typeof source[key] === 'object' &&
        source[key] !== null &&
        !Array.isArray(source[key]) &&
        typeof target[key] === 'object' &&
        target[key] !== null &&
        !Array.isArray(target[key])
      ) {
        // Both are objects, merge recursively
        result[key] = deepMerge(target[key], source[key]);
      } else {
        // Override with source value
        result[key] = source[key];
      }
    }
  }

  return result as T;
};
